import { useState, useEffect, useCallback, useRef } from 'react';
import minisiteService from '../services/minisiteService';

export const useMinisites = (initialFilters = {}) => {
    const [minisites, setMinisites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState(initialFilters);  // stable — only changes via updateFilters

    // Pagination
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const isFetching = useRef(false);

    /* ── Core fetch ── */
    const fetchPage = useCallback(async (pageNum, currentFilters) => {
        if (isFetching.current) return;
        isFetching.current = true;
        setLoading(true);
        setError(null);

        try {
            const response = await minisiteService.list({ ...currentFilters, page: pageNum });
            // api.js interceptor: returns response.data (the JSON body)
            // Body shape: { status, data, message, code }
            // "data" key holds either:
            //   (a) flat array  [...]                  — when paginate() result is serialised via responseJson
            //   (b) envelope    { data:[...], meta:{} } — when we build it manually in the controller
            const body = response;                               // interceptor already returned response.data
            const rawData = body.data;                           // the actual payload

            let items, currentPage, lastPage;

            if (Array.isArray(rawData)) {
                // Shape (a): flat array — no pagination meta
                items = rawData;
                currentPage = pageNum;
                lastPage = pageNum;   // treat as single page (no more)
            } else if (rawData && typeof rawData === 'object') {
                // Shape (b): paginated envelope { data:[], meta:{} }
                items = Array.isArray(rawData.data) ? rawData.data : [];
                currentPage = rawData.meta?.current_page ?? pageNum;
                lastPage = rawData.meta?.last_page ?? pageNum;
            } else {
                items = []; currentPage = pageNum; lastPage = pageNum;
            }

            setMinisites(prev => pageNum === 1 ? items : [...prev, ...items]);
            setHasMore(currentPage < lastPage);
            setPage(currentPage);
        } catch (err) {
            setError(err?.message || 'Failed to load minisites');
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, []); // no deps — receives all it needs as arguments

    /* ── Initial load ── */
    useEffect(() => {
        setPage(1);
        setMinisites([]);
        setHasMore(true);
        isFetching.current = false; // reset guard so the fetch actually runs
        fetchPage(1, filters);
    }, [filters, fetchPage]);

    /* ── Load next page (IntersectionObserver sentinel) ── */
    const loadMore = useCallback(() => {
        if (!loading && hasMore && !isFetching.current) {
            fetchPage(page + 1, filters);
        }
    }, [loading, hasMore, page, filters, fetchPage]);

    const updateFilters = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    /* ── CRUD — optimistic local updates ── */
    const createMinisite = useCallback(async (payload) => {
        setLoading(true);
        try {
            const response = await minisiteService.create(payload);
            const newMinisite = response.data;
            setMinisites(prev => [newMinisite, ...prev]);
            return response;
        } catch (err) {
            throw err; // 422 handled by the form
        } finally {
            setLoading(false);
        }
    }, []);

    const updateMinisite = useCallback(async (id, payload) => {
        setLoading(true);
        try {
            const response = await minisiteService.update(id, payload);
            const updatedMinisite = response.data;
            setMinisites(prev => prev.map(m => m.id === id ? updatedMinisite : m));
            return response;
        } catch (err) {
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteMinisite = useCallback(async (id) => {
        setLoading(true);
        try {
            await minisiteService.remove(id);
            setMinisites(prev => prev.filter(m => m.id !== id));
        } catch (err) {
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const publishMinisite = useCallback(async (id) => {
        setLoading(true);
        try {
            const response = await minisiteService.publish(id);
            const updatedMinisite = response.data;
            setMinisites(prev => prev.map(m => m.id === id ? updatedMinisite : m));
            return response;
        } catch (err) {
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const syncMinisitePages = useCallback(async (id, pagesData) => {
        setLoading(true);
        try {
            const response = await minisiteService.syncPages(id, pagesData);
            const updatedMinisite = response.data;
            setMinisites(prev => prev.map(m => m.id === id ? updatedMinisite : m));
            return response;
        } catch (err) {
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const setMinisiteHomePage = useCallback(async (id, pageId) => {
        setLoading(true);
        try {
            const response = await minisiteService.setHomePage(id, pageId);
            const updatedMinisite = response.data;
            setMinisites(prev => prev.map(m => m.id === id ? updatedMinisite : m));
            return response;
        } catch (err) {
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateMinisitePage = useCallback(async (id, pageId, payload) => {
        try {
            const response = await minisiteService.updatePage(id, pageId, payload);
            const updatedPage = response.data;
            setMinisites(prev => prev.map(m => {
                if (m.id === id) {
                    return {
                        ...m,
                        pages: m.pages.map(p => p.id === pageId ? { ...p, content: updatedPage.content } : p)
                    };
                }
                return m;
            }));
            return response;
        } catch (err) {
            throw err;
        }
    }, []);

    const saveMinisitePageContent = useCallback(async (minisiteId, pageId, content) => {
        try {
            const response = await minisiteService.savePageContent(minisiteId, pageId, content);
            // Keep local cache in sync so reopening the modal shows the saved content
            // instead of the stale value loaded at initial fetch.
            setMinisites(prev => prev.map(m => {
                if (m.id !== minisiteId) return m;
                return {
                    ...m,
                    pages: m.pages.map(p => p.id === pageId ? { ...p, content } : p),
                };
            }));
            return response;
        } catch (err) {
            throw err;
        }
    }, []);

    const saveTeam = useCallback(async (minisiteId, pageId, payload) => {
        try {
            const response = await minisiteService.saveTeam(minisiteId, pageId, payload);
            return response;
        } catch (err) {
            throw err;
        }
    }, []);

    const deleteTeamMember = useCallback(async (minisiteId, pageId, memberId) => {
        try {
            const response = await minisiteService.deleteTeamMember(minisiteId, pageId, memberId);
            return response;
        } catch (err) {
            throw err;
        }
    }, []);

    const saveServices = useCallback(async (minisiteId, pageId, payload) => {
        try {
            const response = await minisiteService.saveServices(minisiteId, pageId, payload);
            return response;
        } catch (err) {
            throw err;
        }
    }, []);

    const deleteService = useCallback(async (minisiteId, pageId, serviceId) => {
        try {
            const response = await minisiteService.deleteService(minisiteId, pageId, serviceId);
            return response;
        } catch (err) {
            throw err;
        }
    }, []);

    const saveDocuments = useCallback(async (minisiteId, pageId, payload) => {
        try {
            const response = await minisiteService.saveDocuments(minisiteId, pageId, payload);
            return response;
        } catch (err) {
            throw err;
        }
    }, []);

    const deleteDocument = useCallback(async (minisiteId, pageId, documentId) => {
        try {
            const response = await minisiteService.deleteDocument(minisiteId, pageId, documentId);
            return response;
        } catch (err) {
            throw err;
        }
    }, []);

    return {
        minisites,
        loading,
        error,
        hasMore,
        loadMore,
        filters,
        updateFilters,
        createMinisite,
        updateMinisite,
        deleteMinisite,
        publishMinisite,
        syncMinisitePages,
        setMinisiteHomePage,
        updateMinisitePage,
        saveMinisitePageContent,
        saveTeam,
        deleteTeamMember,
        saveServices,
        deleteService,
        saveDocuments,
        deleteDocument,
    };
};

export default useMinisites;
